import {
  Box,
  Flex,
  Icon,
  Badge,
  List,
  ListItem,
  Paragraph,
  Popover,
  PopoverContent,
  PopoverContentHeader,
  PopoverTrigger,
  ProgressBar,
  Spinner,
  Stack,
} from '@customerio/pluma-components/react';
import { PlumaProgressBar } from '@customerio/pluma-components/css/components';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { isEmptyString } from '@/utils/string';
import { getPasswordScore, type PasswordLabelColor } from './score';

function indicatorColorForLabel(labelColor: PasswordLabelColor): string {
  switch (labelColor) {
    case 'green':
      return themeVars.color['icon-success'];
    case 'yellow':
      return themeVars.color['icon-caution'];
    case 'red':
      return themeVars.color['icon-critical'];
    case 'grey':
      return themeVars.color['icon-subtle'];
    default: {
      const _exhaustive: never = labelColor;
      return _exhaustive;
    }
  }
}

type PasswordStrengthMeterProps = {
  /** zxcvbn score 0–4. Omit to hide the bar and show a neutral label. */
  score: number | undefined;
  isLoading?: boolean;
  warning?: string | null;
  suggestions?: string[];
};

type StrengthLabelProps = {
  label: string;
  labelColor: PasswordLabelColor;
  warning?: string | null;
  suggestions?: string[];
};

function StrengthLabel({ label, labelColor, warning, suggestions }: StrengthLabelProps) {
  const hasWarning = !isEmptyString(warning);
  const hasSuggestions = suggestions != null && suggestions.length > 0;
  const hasFeedback = hasWarning || hasSuggestions;

  return (
    <Box flexShrink="0">
      <Flex gap="050" alignItems="center">
        <Badge color={labelColor}>{label}</Badge>
        {hasFeedback ? (
          // Popover, not Tooltip: a warning plus a list of suggestions is
          // long-form content that exceeds Pluma's tooltip guideline. Opens on
          // click; `type="button"` keeps it from submitting a surrounding form.
          <Popover placement="bottom-end">
            <PopoverTrigger type="button" cursor="pointer" aria-label="Password strength details">
              <Icon name="help" size="sm" color="subtle" />
            </PopoverTrigger>
            <PopoverContent p="200">
              <Stack gap="100" style={{ maxWidth: '280px' }}>
                <PopoverContentHeader>Improve your password</PopoverContentHeader>
                {hasWarning ? <Paragraph>{warning}</Paragraph> : null}
                {hasSuggestions ? (
                  <List>
                    {suggestions.map((suggestion) => (
                      <ListItem key={suggestion}>{suggestion}</ListItem>
                    ))}
                  </List>
                ) : null}
              </Stack>
            </PopoverContent>
          </Popover>
        ) : null}
      </Flex>
    </Box>
  );
}

export function PasswordStrengthMeter({ score, isLoading = false, warning, suggestions }: PasswordStrengthMeterProps) {
  const { label, labelColor } = getPasswordScore(score);
  const showMeter = typeof score === 'number';

  return (
    <Flex gap="100" alignItems="center">
      {showMeter ? (
        <ProgressBar
          value={score ?? 0}
          max={4}
          size="sm"
          ariaLabel="Password strength"
          data-testid="password-strength-meter-bar"
          style={{
            ...assignInlineVars({
              [PlumaProgressBar.vars.indicatorBackgroundColor]: indicatorColorForLabel(labelColor),
            }),
            // Dim the stale bar while re-evaluating so its fill doesn't read as the current score.
            opacity: isLoading ? 0.5 : undefined,
          }}
        />
      ) : null}

      {isLoading ? (
        <Spinner data-testid="password-strength-spinner" />
      ) : (
        <StrengthLabel label={label} labelColor={labelColor} warning={warning} suggestions={suggestions} />
      )}
    </Flex>
  );
}
